import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Users, Video, Clock, CheckSquare, Bell, BookOpen, ArrowRight, HelpCircle, History, MessageCircle, PhoneCall, MonitorSmartphone } from "lucide-react";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

const GoogleHangouts = () => {
  const faqData = [
    {
      question: "What happened to Google Hangouts?",
      answer: "Google Hangouts was phased out and fully discontinued for consumer users in November 2022. Users were migrated to Google Chat and Google Meet, which replaced Hangouts' messaging and video calling features respectively."
    },
    {
      question: "Why did Google Hangouts shut down?",
      answer: "Google decided to split Hangouts into two specialized products: Google Chat for messaging and Google Meet for video conferencing. This allowed them to better compete with Slack, Microsoft Teams, and Zoom in their respective categories."
    },
    {
      question: "What is the best Google Hangouts alternative for study groups?",
      answer: "CramStudyWith.me is an excellent alternative for virtual study sessions. It offers Study Stream for video collaboration, group study rooms, task management, and productivity tools specifically designed for learning environments."
    },
    {
      question: "Can I still have video calls like Google Hangouts?",
      answer: "Yes! CramStudyWith.me's Study Stream feature provides video collaboration designed specifically for studying. Unlike general video calling, it includes study-focused features like timers, task management, and productivity tracking."
    },
    {
      question: "Is CramStudyWith.me free to use?",
      answer: "Yes, CramStudyWith.me offers free access to core features including Study Stream, Study Timer, Task Management, and group study capabilities. It's a comprehensive platform for students and learners."
    },
    {
      question: "What features does CramStudyWith.me offer compared to Hangouts?",
      answer: "CramStudyWith.me offers Study Stream for live video sessions, group study rooms for collaborative learning, task management for organizing projects, reminders for deadlines, and productivity timers - all focused on educational success."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Google Hangouts Alternative - Free Study Rooms | CramStudyWith.me",
    "description": "Looking for a Google Hangouts alternative? Hangouts is discontinued but CramStudyWith.me offers free Study Stream, Study Timer, and Task Management for video study sessions.",
    "url": "https://cramstudywith.me/google-hangouts/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CramStudyWith.me",
      "url": "https://cramstudywith.me"
    },
    "about": {
      "@type": "Thing",
      "name": "Google Hangouts Alternative"
    },
    "mentions": {
      "@type": "SoftwareApplication",
      "name": "Google Hangouts",
      "applicationCategory": "CommunicationApplication"
    },
    "keywords": "Google Hangouts alternative, Google Hangouts replacement, Google Hangouts shutdown, video calling, study groups, online collaboration"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CramStudyWith.me",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "ratingCount": "1000"
    },
    "featureList": [
      "Study Stream - Live video study sessions",
      "Study Timer - Pomodoro and custom timers",
      "Task Management - Organize assignments",
      "Group Study Rooms - Collaborative learning",
      "Reminders - Never miss deadlines",
      "Flash Cards - Active recall learning",
      "Quizzes - Test your knowledge",
      "Video Collaboration - Study together"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "CramStudyWith.me"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Google Hangouts Alternative - Free Study Rooms | CramStudyWith.me</title>
        <meta name="description" content="Looking for a Google Hangouts alternative? Hangouts is discontinued but CramStudyWith.me offers free Study Stream, Study Timer, and Task Management for video study sessions." />
        <meta name="keywords" content="Google Hangouts alternative, Google Hangouts replacement, Google Hangouts shutdown, video calling, study groups, online collaboration, CramStudyWith.me" />
        <link rel="canonical" href="https://cramstudywith.me/google-hangouts/" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full mb-6">
              <PhoneCall className="h-4 w-4" />
              <span className="text-sm font-medium">Google Hangouts Discontinued November 2022</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Google Hangouts Alternative
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Google Hangouts is no longer available. Discover CramStudyWith.me - the modern platform for <Link to="/study-with-me/" className="text-primary hover:underline">video study sessions</Link> with collaborative rooms, productivity tools, and learning features.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/studystream/">
                <Button size="lg" className="gap-2">
                  <Video className="h-5 w-5" />
                  Try Study Stream Free
                </Button>
              </Link>
              <Link to="/google-hangouts/history/">
                <Button size="lg" variant="outline" className="gap-2">
                  <History className="h-5 w-5" />
                  Google Hangouts History
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* What Happened Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-6 text-center">What Happened to Google Hangouts?</h2>
              <div className="bg-card rounded-lg p-8 shadow-lg">
                <p className="text-lg text-muted-foreground mb-4">
                  Google Hangouts was <strong>discontinued for consumer users in November 2022</strong> after nearly a decade of service. Google migrated users to two replacement products: Google Chat for messaging and Google Meet for video calling.
                </p>
                <p className="text-lg text-muted-foreground mb-4">
                  The decision was part of Google's strategy to create specialized products that could better compete with dedicated platforms like Slack, Zoom, and Microsoft Teams in their respective categories.
                </p>
                <p className="text-lg text-muted-foreground">
                  If you're looking for video collaboration tools designed specifically for studying, <Link to="/online-workspace/" className="text-primary hover:underline">CramStudyWith.me</Link> offers modern features including <Link to="/productivity-tool/" className="text-primary hover:underline">productivity tools</Link>, study-focused video rooms, and <Link to="/study-with-friends/" className="text-primary hover:underline">group study capabilities</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Comparison */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-12 text-center">Google Hangouts vs CramStudyWith.me</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Video className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Study Stream</CardTitle>
                  <CardDescription>Purpose-built video study</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Unlike general video calling, <Link to="/studystream/" className="text-primary hover:underline">Study Stream</Link> is designed specifically for studying with productivity features built in.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Users className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Group Study Rooms</CardTitle>
                  <CardDescription>Virtual study spaces</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Create focused <Link to="/group-study/" className="text-primary hover:underline">study groups</Link> with video, audio, and collaborative tools - more than just video calling.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Clock className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Study Timer</CardTitle>
                  <CardDescription>Pomodoro technique</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Stay focused with our <Link to="/study-timer/" className="text-primary hover:underline">Study Timer</Link> - a feature Hangouts never had for productive study sessions.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <CheckSquare className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Task Management</CardTitle>
                  <CardDescription>Organize study projects</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Manage assignments and projects with our <Link to="/task-management/" className="text-primary hover:underline">Task Management</Link> system integrated with video study.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Bell className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Smart Reminders</CardTitle>
                  <CardDescription>Never miss deadlines</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Get intelligent <Link to="/reminders/" className="text-primary hover:underline">reminders</Link> for study sessions - built-in productivity notifications.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <BookOpen className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Learning Tools</CardTitle>
                  <CardDescription>Flash cards & quizzes</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Access <Link to="/flash-cards/" className="text-primary hover:underline">flash cards</Link> and <Link to="/quizzes/" className="text-primary hover:underline">quizzes</Link> for active learning alongside video study.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Blog Links Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">Learn More About Video Studying</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">How Turning On My Camera Boosted My Productivity</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/blog/science-behind-study-with-me/" className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">The Science Behind Study With Me</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/blog/top-10-digital-tools-for-academic-success/" className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">Top 10 Digital Tools for Academic Success</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-center gap-3 mb-8">
                <HelpCircle className="h-8 w-8 text-primary" />
                <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Frequently Asked Questions
                </h2>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {faqData.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary to-secondary">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready for Purpose-Built Video Studying?
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
              Join thousands of students using CramStudyWith.me for focused video study sessions and collaborative learning.
            </p>
            <Link to="/studystream/">
              <Button size="lg" variant="secondary" className="gap-2">
                Get Started Free
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <RelatedAlternatives currentPlatform="Google Hangouts" />

      <Footer />
    </div>
  );
};

export default GoogleHangouts;